// --- external
import { computed, ref } from "vue";
import { useActor } from "@xstate/vue";
import { waitFor } from "xstate/lib/waitFor";

// --- internal
import { useBrand } from "../brand";
import {
  stateMatches,
  contextMatches,
  DEBOUNCE_DELAY,
  contextValue,
  useImageUrl,
  useContext
} from "../../utils";

// --- utils
import {
  add,
  compact,
  debounce,
  find,
  forEach,
  get,
  isArray,
  isEmpty,
  isEqual,
  set,
  some,
  subtract
} from "lodash-es";

// --- types
import type { ActorRef } from "xstate";
import type {
  Product,
  ProductDetails,
  ProductModel,
  TermDetails,
  SubproductDetails,
  ProductConfigContext
} from "./";

// -----------------------------------------------------------------------------

/**
 * A composable function that provides functionality and state management for product configuration.
 * It integrates various aspects of product customisation, such as quantity, terms, attributes, and options,
 * while managing the underlying state using an actor-based state management system.
 * @param {ActorRef<any>} service - The actor reference representing the product configuration state machine.
 * @returns The {@link UseProductConfig} composable methods and state for product configuration.
 */
export const useProductConfig = (service: ActorRef<any>) => {
  const { includesTax } = useBrand();

  const { state, send } = useActor(service);
  const model = useContext<ProductModel>(state, "model");
  const lookups = useContext<ProductConfigContext["lookups"]>(state, "lookups");
  const raw = computed(() => ({
    product: contextValue<ProductConfigContext["rawProduct"]>(
      state,
      "rawProduct"
    ),
    basketProduct: contextValue<ProductConfigContext["rawBasketProduct"]>(
      state,
      "rawBasketProduct"
    ),
    provisionFields: contextValue<ProductConfigContext["rawProvisionFields"]>(
      state,
      "rawProvisionFields"
    )
  }));
  const id = computed(() => service.id);
  const touched = ref(false);

  // syntactic sugar
  const productDetails = useContext<ProductDetails>(state, "lookups.product");

  const productImage = (size: string = "400x400"): string | undefined => {
    return useImageUrl(productDetails.value?.imgUrl, size);
  };

  const product = useContext<Product>(state, "product");
  const title = useContext<string>(state, "product.title");
  const terms = useContext<TermDetails[]>(state, "lookups.terms");
  const attributes = useContext<SubproductDetails[]>(
    state,
    "lookups.attributes"
  );
  const options = useContext<SubproductDetails[]>(state, "lookups.options");
  const fields = useContext<Record<string, any>>(
    state,
    "lookups.provisionFields"
  );

  // ---
  const errors = useContext<Product["errors"]>(state, "error.message");

  const validationErrors = useContext<Product["errors"]>(state, "error.data");
  const additionalErrors = useContext<Product["errors"]>(
    state,
    "errorExternal"
  );

  const meta = computed<UseProductConfigMeta>(() => ({
    isLoading: stateMatches(state, ["subscribing", "loading"]),
    isNew: !contextMatches(state, ["basketProduct"]),
    isDirty: !isEqual(
      contextValue<ProductConfigContext["model"]>(state, "model"),
      contextValue<ProductConfigContext["baseModel"]>(state, "baseModel")
    ),
    isTouched: touched.value,
    showErrors:
      contextMatches(state, ["error"]) && contextMatches(state, ["attempts"]),

    hasErrors:
      stateMatches(state, ["error", "available.invalid", "available.error"]) ||
      contextMatches(state, ["error"]),

    isConfigurable:
      (terms.value?.length ?? 0) > 1 ||
      contextMatches(state, [
        "lookups.attributes",
        "lookups.options",
        "lookups.provisionFields.properties"
      ]),
    isInvalid: stateMatches(state, ["available.invalid"]),
    isCalculating: contextMatches(state, ["lookups.prices.calculating"]),
    isProcessing: stateMatches(state, ["refreshing", "processing"]),
    isAvailable: stateMatches(state, ["available", "refreshing", "processing"]),
    isUnavailable: stateMatches(state, ["error", "available.error"]),
    isComplete: stateMatches(state, ["complete"]),
    isDone: !state.value || state.value?.done,

    // ---
    hasProvisioning: !isEmpty(
      state.value.context?.lookups?.provisionFields?.properties
    ),
    hasAttributes: !isEmpty(state.value.context?.lookups?.attributes),
    hasOptions: !isEmpty(state.value.context?.lookups?.options),
    hasTerms: !isEmpty(state.value.context?.lookups?.terms),
    hasTaxIncluded: includesTax.value
  }));

  // --
  async function setValues(
    type:
      | "SET.QUANTITY"
      | "SET.TERM"
      | "SET.ATTRIBUTES"
      | "SET.OPTIONS"
      | "SET.PROVISIONING",
    data: Partial<ProductModel>
  ) {
    touched.value = true;
    send({ type, data });

    return waitFor(
      service,
      state => ["available.valid", "available.invalid"].some(state.matches),
      { timeout: 60_000 }
    );
  }

  // --- QUANTITY

  const updateQuantity = debounce(async (value?: number): Promise<void> => {
    setValues("SET.QUANTITY", {
      quantity: value
    });
  }, DEBOUNCE_DELAY);

  const incrementQuantity = debounce(async (value?: number): Promise<void> => {
    // sanity check
    if (!lookups.value?.product?.quantifiable) return;

    const qty = get(model.value, "quantity", 0);

    // emit the event
    return updateQuantity(add(qty, lookups.value.product?.step || 1));
  }, DEBOUNCE_DELAY);

  const decrementQuantity = debounce(async (value?: number): Promise<void> => {
    // sanity check
    if (!lookups.value?.product?.quantifiable) return;

    const qty = get(model.value, "quantity", 0);

    // emit the event
    return updateQuantity(subtract(qty, lookups.value.product?.step || 1));
  }, DEBOUNCE_DELAY);

  // --- TERMS

  function isSelectedTerm(value: number): boolean {
    return isEqual(value, model.value?.term);
  }

  const updateTerm = async (value: number): Promise<void> =>
    setValues("SET.TERM", {
      term: value
    });

  // --- ATTRIBUTES

  function isSelectedAttribute(attributeId: string, value: string): boolean {
    return some(model.value?.attributes?.[attributeId], ["productId", value]);
  }

  async function setAttributes(
    attribute: SubproductDetails,
    values: string | string[]
  ): Promise<void> {
    const attributes = model.value?.attributes ?? {};
    set(attributes, attribute.id, {}); // reset all previous attributes

    const safeValues = compact(isArray(values) ? values : [values]);

    forEach(safeValues, value => {
      set(attributes, [attribute.id, value], {
        productId: value
      });
    });

    // emit the event
    return setValues("SET.ATTRIBUTES", { attributes });
  }

  // --- OPTIONS

  function isSelectedOption(optionId: string, value: string): boolean {
    return some(model.value?.options?.[optionId], ["productId", value]);
  }

  async function setOptions(
    option: SubproductDetails,
    values: string | string[]
  ): Promise<void> {
    const options = model.value?.options ?? {};
    const previousStates = get(options, option.id, {}); // keep the previous state to restore quantity

    set(options, option.id, {}); // reset all previous options

    const safeValues = compact(isArray(values) ? values : [values]);
    forEach(safeValues, value => {
      const quantity = get(previousStates, [value, "quantity"]);

      const optionValue: { productId: string; quantity?: number } = {
        productId: value
      };

      if (quantity) {
        optionValue.quantity = quantity;
      }

      set(options, [option.id, value], optionValue);
    });

    // emit the event
    return setValues("SET.OPTIONS", { options });
  }

  async function updateOptionQuantity(
    option: SubproductDetails,
    valueId: string,
    qty: number
  ): Promise<void> {
    // sanity check
    const value = find(option.values, ["id", valueId]);
    if (!value?.quantifiable) return;
    const options = model.value?.options ?? {};
    set(options, [option.id, value.id, "quantity"], qty);
    // emit the event
    return setValues("SET.OPTIONS", { options });
  }

  async function incrementOption(
    option: SubproductDetails,
    valueId: string
  ): Promise<void> {
    // sanity check
    const value = find(option.values, ["id", valueId]);
    if (!value?.quantifiable) return;
    const options = model.value?.options ?? {};
    const qty = get(options, [option.id, value.id, "step"], 0);
    set(options, [option.id, value.id, "quantity"], add(qty, value?.step || 1));

    // emit the event
    return setValues("SET.OPTIONS", { options });
  }

  async function decrementOption(
    option: SubproductDetails,
    valueId: string
  ): Promise<void> {
    // sanity check
    const value = find(option.values, ["id", valueId]);
    if (!value?.quantifiable) return;

    const options = model.value?.options ?? {};
    const qty = get(options, [option.id, value.id, "step"], 0);
    set(
      options,
      [option.id, value.id, "quantity"],
      subtract(qty, value?.step || 1)
    );

    // emit the event
    return setValues("SET.OPTIONS", { options });
  }

  // --- PROVISIONING

  async function setProvisioningFields(
    values: Record<string, any>
  ): Promise<void> {
    // emit the event
    return setValues("SET.PROVISIONING", { provisionFields: values });
  }

  function getProvisioningField(field: string): any {
    return get(model.value, ["provisionFields", field]);
  }

  // ---------------------------------------------------------------------------
  return {
    id,
    state,
    service,
    // context,
    errors,
    validationErrors,
    additionalErrors,
    meta,
    // ---
    lookups,
    raw,
    title,
    // productDetails,
    productImage,
    terms,
    options,
    attributes,
    fields,
    // ---
    model,
    product,
    // ---
    updateQuantity,
    incrementQuantity,
    decrementQuantity,
    // ---
    updateTerm,
    isSelectedTerm,
    // ---
    isSelectedAttribute,
    setAttributes,
    // ---
    isSelectedOption,
    setOptions,
    updateOptionQuantity,
    incrementOption,
    decrementOption,
    // ---
    setProvisioningFields,
    getProvisioningField,
    // ---
    reset: () => send({ type: "RESET" }),
    onDone: () =>
      new Promise(resolve => {
        const sub = service.subscribe(state => {
          if (state.done) {
            resolve(state.context);
            sub.unsubscribe();
          }
        });
      })
  };
};

/**
 * The return type of the {@link useProductConfig} composable function.
 */
export type UseProductConfig = ReturnType<typeof useProductConfig>;

/**
 * Represents the metadata related to a product configuration process.
 *
 * This type provides a set of boolean flags that indicate various states and conditions
 * during the product configuration lifecycle. It encapsulates important information such
 * as loading states, validation status, availability, completion, and additional feature-related flags.
 */
export type UseProductConfigMeta = {
  isLoading: boolean;
  isNew: boolean;
  isDirty: boolean;
  isTouched: boolean;
  showErrors: boolean;
  hasErrors: boolean;
  isConfigurable: boolean;
  isInvalid: boolean;
  isProcessing: boolean;
  isCalculating: boolean;
  isAvailable: boolean;
  isUnavailable: boolean;
  isComplete: boolean;
  isDone: boolean;
  // ---
  hasProvisioning: boolean;
  hasAttributes: boolean;
  hasOptions: boolean;
  hasTerms: boolean;
  hasTaxIncluded: boolean;
};
